const HEX_ESCAPE_LIMIT = 6;
const MAX_CODE_POINT = 0x10ffff;
const SURROGATE_START = 0xd800;
const SURROGATE_END = 0xdfff;
const COMMENT_DELIMITER_LENGTH = 2;
const CRLF_LENGTH = 2;
const RESOURCE_FUNCTIONS = new Set(['url', 'src', 'image', 'image-set', '-webkit-image-set']);
const isWhitespace = (character: string): boolean => /^[\t\n\f\r ]$/.test(character);
const isNewline = (character: string): boolean => /^[\n\f\r]$/.test(character);
const isName = (character: string): boolean => /^[-_a-z\d\u0080-\uffff]$/i.test(character);

const afterComment = (css: string, start: number): number => {
    const end = css.indexOf('*/', start + COMMENT_DELIMITER_LENGTH);
    return end < 0 ? css.length : end + COMMENT_DELIMITER_LENGTH;
};

const escapedCodePoint = (css: string, start: number): { value: string; end: number } => {
    let end = start + 1;
    if (end >= css.length) return { value: '\ufffd', end };
    if (isNewline(css[end])) {
        return { value: '', end: end + (css[end] === '\r' && css[end + 1] === '\n' ? CRLF_LENGTH : 1) };
    }
    if (/[a-f\d]/i.test(css[end])) {
        const first = end;
        while (end < css.length && end - first < HEX_ESCAPE_LIMIT && /[a-f\d]/i.test(css[end])) end += 1;
        const point = parseInt(css.slice(first, end), 16);
        if (isWhitespace(css[end])) end += css[end] === '\r' && css[end + 1] === '\n' ? CRLF_LENGTH : 1;
        const invalid = point === 0 || point > MAX_CODE_POINT || (point >= SURROGATE_START && point <= SURROGATE_END);
        return { value: invalid ? '\ufffd' : String.fromCodePoint(point), end };
    }
    const value = String.fromCodePoint(css.codePointAt(end)!);
    return { value, end: end + value.length };
};

const afterString = (css: string, start: number): number => {
    const quote = css[start];
    let index = start + 1;
    while (index < css.length) {
        if (css[index] === quote) return index + 1;
        if (isNewline(css[index])) return index;
        index = css[index] === '\\' ? escapedCodePoint(css, index).end : index + 1;
    }
    return index;
};

const identifier = (css: string, start: number): { value: string; end: number } => {
    let value = '';
    let end = start;
    while (end < css.length) {
        if (css[end] === '\\') {
            const escaped = escapedCodePoint(css, end);
            value += escaped.value;
            end = escaped.end;
        } else if (isName(css[end])) {
            value += css[end];
            end += 1;
        } else if (css[end] === '/' && css[end + 1] === '*') {
            end = afterComment(css, end);
        } else break;
    }
    return { value, end };
};

const afterFunction = (css: string, opening: number): number => {
    let depth = 1;
    let index = opening + 1;
    while (index < css.length && depth > 0) {
        if (css[index] === '"' || css[index] === "'") index = afterString(css, index);
        else if (css[index] === '/' && css[index + 1] === '*') index = afterComment(css, index);
        else if (css[index] === '\\') index = escapedCodePoint(css, index).end;
        else {
            if (css[index] === '(') depth += 1;
            if (css[index] === ')') depth -= 1;
            index += 1;
        }
    }
    return index;
};

// eslint-disable-next-line import/prefer-default-export
export const stripCandidateResourceFunctions = (
    css: string,
    preservePlainUrls = false,
): { css: string; blockedResources: number } => {
    const output: string[] = [];
    let blockedResources = 0;
    let retainedStart = 0;
    let index = 0;
    while (index < css.length) {
        if (css[index] === '/' && css[index + 1] === '*') index = afterComment(css, index);
        else if (css[index] === '"' || css[index] === "'") index = afterString(css, index);
        else if (isName(css[index]) || css[index] === '\\') {
            const name = identifier(css, index);
            let opening = name.end;
            while (opening < css.length) {
                if (isWhitespace(css[opening])) opening += 1;
                else if (css[opening] === '/' && css[opening + 1] === '*') opening = afterComment(css, opening);
                else break;
            }
            const plainUrl = preservePlainUrls && /^url$/i.test(css.slice(index, name.end))
                && css[name.end] === '(';
            if (css[opening] === '(' && RESOURCE_FUNCTIONS.has(name.value.toLowerCase()) && !plainUrl) {
                const end = afterFunction(css, opening);
                output.push(css.slice(retainedStart, index), 'none');
                retainedStart = end;
                index = end;
                blockedResources += 1;
            } else index = plainUrl ? afterFunction(css, opening) : name.end;
        } else index += 1;
    }
    output.push(css.slice(retainedStart));
    return { css: output.join(''), blockedResources };
};
